import { useEffect, useMemo, useState } from 'react';
import { logout } from '../../auth';
import { get, post } from '../../services/api';
import { formatarOpcaoFilial } from '../../utils/filial';
import { useNavigate } from 'react-router-dom';

import Select from 'react-select';
import AdminSection from '../AdminSection/AdminSection';
import AdminTable from '../AdminTable/AdminTable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

import './ConsultaSalarioGerentes.css';
import {
    buildLinhaRemuneracaoGerente,
    getColunasRemuneracaoGerentes,
    getLinhasPaginadas,
    getTotalPaginas,
    LINHAS_POR_PAGINA
} from './ConsultaSalarioGerentes.utils';

export default function ConsultaSalarioGerentes({ setNotifier }) {
    const navigate = useNavigate();
    const [ authTokenState, setAuthTokenState ] = useState(null);

    const selectStyle = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            background: 'unset',
            borderColor: '#DCDCDC',
            borderRadius: '4px',
            boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : 'none',
            minHeight: '38px'
        }),
        valueContainer: base => ({ ...base, padding: '0 8px' }),
        indicatorsContainer: base => ({ ...base, height: '38px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    }), []);

    const mensagemNenhumaOpcaoAlternativa = () => 'Nenhuma opção disponível';

    // Options
    const [ optionsFilial, setOptionsFilial ] = useState([]);
    const [ optionsCluster, setOptionsCluster ] = useState([]);

    // Generate period options (current month and next month)
    const currentDate = new Date();
    const optionsPeriodo = [];
    for (let i = -1; i <= 1; i++) {
        let mes = currentDate.getMonth() + 1 + i;
        let ano = currentDate.getFullYear();
        if (mes <= 0) { mes += 12; ano -= 1; }
        if (mes > 12) { mes -= 12; ano += 1; }
        const mesLabel = String(mes).padStart(2, '0');
        optionsPeriodo.push({ label: `${mesLabel}/${ano}`, value: `${mes}-${ano}`, mes, ano });
    }

    // Selected filters
    const [ filialSelecionada, setFilialSelecionada ] = useState(null);
    const [ nomeGerente, setNomeGerente ] = useState('');
    const [ periodoSelecionado, setPeriodoSelecionado ] = useState(optionsPeriodo[0]);

    // Data
    const [ gerentes, setGerentes ] = useState(null);
    const [ editandoRow, setEditandoRow ] = useState(null);
    const [ clusterEditado, setClusterEditado ] = useState(null);
    const [ page, setPage ] = useState(1);

    // Search
    async function buscarDados() {
        try {
            const params = new URLSearchParams();
            if (filialSelecionada && filialSelecionada.value !== 0) {
                params.append('idfiliais', filialSelecionada.value);
            } else {
                params.append('idfiliais', 0);
            }
            params.append('mes', periodoSelecionado.mes);
            params.append('ano', periodoSelecionado.ano);
            if (nomeGerente) params.append('nome_gerente', nomeGerente);

            const response = await get(`/admin/remuneracaogerentes?${params.toString()}`, null, authTokenState);
            setGerentes(response);
            setPage(1);
        } catch (error) {
            console.error('Erro ao buscar gerentes:', error);
        }
    }

    function getRowKey(gerente) {
        return `${gerente.idfolhapagamento}_${gerente.idfilial}`;
    }

    function iniciarEdicao(gerente) {
        setEditandoRow(getRowKey(gerente));
        const currentCluster = optionsCluster.find(c => String(c.value) === String(gerente.idcluster));
        setClusterEditado(currentCluster || null);
    }

    function cancelarEdicao() {
        setEditandoRow(null);
        setClusterEditado(null);
    }

    async function salvarCluster(gerente) {
        try {
            if (!clusterEditado) {
                setNotifier(false, 'Selecione um cluster!');
                return;
            }
            const idUsuario = localStorage.getItem('idusuario');

            const response = await post('/admin/atualizarclustergerente', {
                idfilial: gerente.idfilial,
                idcluster: clusterEditado.value,
                mes: periodoSelecionado.mes,
                ano: periodoSelecionado.ano,
                idusuario: idUsuario
            }, authTokenState);

            if (response && response.Sucesso) {
                setNotifier(true, response.Mensagem);
                setEditandoRow(null);
                setClusterEditado(null);
                await buscarDados(); // Refresh
            } else {
                let errorMsg = 'Erro ao atualizar cluster';
                if (response && response.response && response.response.data) {
                    errorMsg = response.response.data.Mensagem || errorMsg;
                    if (response.response.data.Error) {
                        errorMsg += ': ' + response.response.data.Error;
                    }
                } else if (response && response.Mensagem) {
                    errorMsg = response.Mensagem;
                } else if (response && response.message) {
                    errorMsg += ': ' + response.message;
                }
                setNotifier(false, errorMsg);
            }
        } catch (error) {
            setNotifier(false, 'Erro ao atualizar cluster: ' + error.message);
            console.error(error);
        }
    }

    const dados = gerentes != null && Array.isArray(gerentes)
        ? gerentes.map((g) => {
            const isEditing = editandoRow === getRowKey(g);
            const linha = buildLinhaRemuneracaoGerente(g);
            linha.rowKey = getRowKey(g);

            return {
                ...linha,
                cluster: isEditing ? (
                    <Select
                        className="react-select cluster-select-inline"
                        classNamePrefix="react-select"
                        options={optionsCluster}
                        value={clusterEditado}
                        onChange={setClusterEditado}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyle}
                        isSearchable={false}
                        placeholder="Cluster"
                    />
                ) : linha.cluster,
                acao: isEditing ? (
                    <div className="action-buttons">
                        <button className="action-btn confirm-btn" onClick={() => salvarCluster(g)}>
                            <CheckCircleIcon style={{ color: '#22C55E', width: '28px', height: '28px' }} />
                        </button>
                        <button className="action-btn cancel-btn" onClick={() => cancelarEdicao()}>
                            <CancelIcon style={{ color: '#EF4444', width: '28px', height: '28px' }} />
                        </button>
                    </div>
                ) : (
                    <button className="action-btn edit-btn" onClick={() => iniciarEdicao(g)}>
                        <EditIcon style={{ color: 'white', width: '14px', height: '14px' }} />
                    </button>
                )
            };
        })
        : null;

    const tableData = {
        atributoKey: 'rowKey',
        colunas: getColunasRemuneracaoGerentes(),
        dados: dados
    };

    const dadosPaginados = getLinhasPaginadas(tableData.dados, page);
    const numberOfPages = getTotalPaginas(tableData.dados);

    useEffect(() => {
        const authToken = localStorage.getItem('adminToken');

        async function fetchFiliais() {
            try {
                const filiais = await get('admin/metas/filial', null, authToken);
                const options = [];

                if (Array.isArray(filiais)) {
                    filiais.forEach(grupo => {
                        const filiaisDoGrupo = grupo.items || grupo.options;
                        if (filiaisDoGrupo) {
                            filiaisDoGrupo.forEach(f => options.push(f));
                        } else {
                            options.push(grupo);
                        }
                    });
                }

                // Ordenar numericamente pelo ID da filial
                options.sort((a, b) => a.value - b.value);

                // Padronizar o label para "ID - Nome" igual fizemos na tabela
                const optionsFormatadas = options.map(formatarOpcaoFilial);

                setOptionsFilial([{ label: 'Todas', value: 0 }, ...optionsFormatadas]);
            } catch (error) {
                console.error('Erro ao buscar filiais:', error);
            }
        }

        async function fetchClusters() {
            try {
                const clusters = await get('/admin/metas/cluster', null, authToken);
                setOptionsCluster(clusters.map(c => ({ label: String(c.id_cluster), value: c.id_cluster })));
            } catch (error) {
                console.error('Erro ao buscar clusters:', error);
            }
        }

        if (authToken == null) {
            logout();
            navigate('/login');
            return;
        }

        setAuthTokenState(authToken);
        fetchFiliais();
        fetchClusters();
    }, [navigate]);

    const uploadButton = (
        <button className="upload-btn" onClick={() => navigate('/admin/remuneracao/gerentes/importacao')}>
            Upload
        </button>
    );

    return <div className="remuneracao-gerentes-page">
        <AdminSection
            etapa={1} titulo={'Consultar Cluster'} acoes={uploadButton}
        >
            <div className='consulta-salario-filtros p-8'>
                <div className='filtro-item'>
                    <label htmlFor="filial">Filial</label>
                    <Select
                        name="filial"
                        className="react-select"
                        classNamePrefix="react-select"
                        options={optionsFilial}
                        value={filialSelecionada}
                        onChange={setFilialSelecionada}
                        placeholder="Todas"
                        menuPortalTarget={document.body}
                        styles={selectStyle}
                        isSearchable={true}
                        noOptionsMessage={mensagemNenhumaOpcaoAlternativa}
                    />
                </div>

                <div className='filtro-item'>
                    <label htmlFor="nomeGerente">Nome gerente</label>
                    <input
                        type="text"
                        name="nomeGerente"
                        placeholder="Todos"
                        value={nomeGerente}
                        onChange={(e) => setNomeGerente(e.target.value)}
                        className="input-nome-gerente"
                    />
                </div>

                <div className='filtro-item'>
                    <label htmlFor="periodo">Período</label>
                    <Select
                        name="periodo"
                        className="react-select"
                        classNamePrefix="react-select"
                        options={optionsPeriodo}
                        value={periodoSelecionado}
                        onChange={setPeriodoSelecionado}
                        placeholder="Selecione"
                        menuPortalTarget={document.body}
                        styles={selectStyle}
                        isSearchable={false}
                        noOptionsMessage={mensagemNenhumaOpcaoAlternativa}
                    />
                </div>

                <div className='filtro-item filtro-botao'>
                    <button className="search-btn" onClick={buscarDados}>Filtrar</button>
                </div>
            </div>
        </AdminSection>

        <AdminSection
            etapa={2} titulo={'Consultar gerentes'}
        >
            <AdminTable
                atributoKey={tableData.atributoKey}
                colunas={tableData.colunas}
                dados={dadosPaginados}
                styleName={'tabela-salario-gerentes'}
                isPaginada={tableData.dados?.length > LINHAS_POR_PAGINA}
                page={page}
                setPage={setPage}
                numberOfPages={numberOfPages}
                setDadosByPage={setPage}
            />
        </AdminSection>
    </div>;
}
